import React, { useEffect, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ShareIcon from '@mui/icons-material/Share';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TwitterIcon from '@mui/icons-material/Twitter';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import Link from 'next/link';

import { useIntl } from '@klokhuis/core/Intl';
import { PageLayout } from '@klokhuis/core/PageLayout';
import { ROUTES } from '@klokhuis/core/Routing';

import {
  getArticleBySlug,
  InsightArticleMeta,
} from './articles';
import styles from './ArticleDetail.module.css';

const ORIGIN = 'https://www.findmyprops.com';

interface Props {
  meta: InsightArticleMeta;
}

export const ArticleDetailPage: React.FC<Props> = ({ meta }) => {
  const { T } = useIntl();
  const article = getArticleBySlug(meta.slug);
  const [copied, setCopied] = useState(false);
  const [pageUrl, setPageUrl] = useState(
    `${ORIGIN}/real-estate-insights/${meta.slug}`,
  );

  useEffect(() => {
    setPageUrl(window.location.href);
  }, []);

  const shareText = encodeURIComponent(meta.title);
  const shareUrl = encodeURIComponent(pageUrl);

  const openShare = (url: string) =>
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=500');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback: do nothing
    }
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: meta.title,
    description: meta.summary,
    url: `${ORIGIN}/real-estate-insights/${meta.slug}`,
    inLanguage: 'en-IN',
    author: {
      '@type': 'Organization',
      name: 'FindMyProps',
      url: ORIGIN,
    },
    publisher: {
      '@type': 'Organization',
      name: 'FindMyProps',
      url: ORIGIN,
      logo: { '@type': 'ImageObject', url: `${ORIGIN}/icon.svg` },
    },
    articleSection: meta.category,
    keywords: meta.tags.join(', '),
    about: {
      '@type': 'Thing',
      name: 'South Indian Real Estate',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: ORIGIN,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Real Estate Insights',
        item: `${ORIGIN}/real-estate-insights`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: meta.title,
        item: `${ORIGIN}/real-estate-insights/${meta.slug}`,
      },
    ],
  };

  if (!article) return null;

  return (
    <PageLayout
      pageTitle={`${meta.title} | FindMyProps Real Estate Insights`}
      metaDescription={meta.summary}
      route={ROUTES.RealEstateInsightsArticle}
      routeParams={{ slug: meta.slug }}
      showPropertyLinksGrid={false}
      jsonLd={[articleJsonLd, breadcrumbJsonLd]}
    >
      <PageLayout.SectionContainer className={styles.root}>

        {/* Breadcrumb */}
        <Box className={styles.breadcrumb}>
          <Link href="/real-estate-insights" passHref legacyBehavior>
            <Box component="a" className={styles.backLink}>
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              {T('article.breadcrumb.back')}
            </Box>
          </Link>
          <Typography variant="caption" color="text.secondary">
            /
          </Typography>
          <Typography variant="caption" color="text.secondary" className={styles.breadcrumbCurrent}>
            {meta.category}
          </Typography>
        </Box>

        <Box className={styles.layout}>
          {/* ── Main article ── */}
          <Box component="article" className={styles.article}>

            {/* Header */}
            <Box className={styles.articleHeader}>
              <Box className={styles.headerBadges}>
                <Chip label={meta.category} color="primary" size="small" />
                <Chip label={meta.audience} color="secondary" size="small" variant="outlined" />
                <Typography variant="caption" color="text.secondary">
                  {meta.readTime}
                </Typography>
              </Box>

              <Typography variant="h3" component="h1" className={styles.title}>
                {meta.title}
              </Typography>

              <Typography variant="body1" className={styles.summary}>
                {meta.summary}
              </Typography>

              <Divider className={styles.headerDivider} />
            </Box>

            {/* Article body */}
            <Box className={styles.articleBody}>
              {article.content}
            </Box>

            {/* Tags */}
            <Box className={styles.tags}>
              {meta.tags.map(tag => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
            </Box>

            {/* Disclaimer */}
            <Box className={styles.disclaimer}>
              <Typography variant="caption">
                {T('article.disclaimer')}
              </Typography>
            </Box>
          </Box>

          {/* ── Sidebar ── */}
          <Box className={styles.sidebar}>

            {/* Share panel */}
            <Box className={styles.sharePanel}>
              <Box className={styles.sharePanelHeader}>
                <ShareIcon sx={{ fontSize: 18 }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  {T('article.share.title')}
                </Typography>
              </Box>

              <Box className={styles.shareButtons}>
                {/* WhatsApp */}
                <Tooltip title={T('article.share.whatsapp')}>
                  <IconButton
                    className={`${styles.shareBtn} ${styles.whatsapp}`}
                    onClick={() =>
                      openShare(
                        `https://api.whatsapp.com/send?text=${shareText}%20${shareUrl}`,
                      )
                    }
                    aria-label={T('article.share.whatsapp')}
                  >
                    <WhatsAppIcon />
                  </IconButton>
                </Tooltip>

                {/* X / Twitter */}
                <Tooltip title={T('article.share.twitter')}>
                  <IconButton
                    className={`${styles.shareBtn} ${styles.twitter}`}
                    onClick={() =>
                      openShare(
                        `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`,
                      )
                    }
                    aria-label={T('article.share.twitter')}
                  >
                    <TwitterIcon />
                  </IconButton>
                </Tooltip>

                {/* LinkedIn */}
                <Tooltip title={T('article.share.linkedin')}>
                  <IconButton
                    className={`${styles.shareBtn} ${styles.linkedin}`}
                    onClick={() =>
                      openShare(
                        `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
                      )
                    }
                    aria-label={T('article.share.linkedin')}
                  >
                    <LinkedInIcon />
                  </IconButton>
                </Tooltip>

                {/* Facebook */}
                <Tooltip title={T('article.share.facebook')}>
                  <IconButton
                    className={`${styles.shareBtn} ${styles.facebook}`}
                    onClick={() =>
                      openShare(
                        `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
                      )
                    }
                    aria-label={T('article.share.facebook')}
                  >
                    <FacebookIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Copy link */}
              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                onClick={handleCopy}
                className={styles.copyBtn}
              >
                {copied ? T('article.share.link_copied') : T('article.share.copy_link')}
              </Button>
            </Box>

            {/* More articles */}
            <Box className={styles.morePanel}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                {T('article.more.title')}
              </Typography>
              {ROUTES && (
                <Link href="/real-estate-insights" passHref legacyBehavior>
                  <Box component="a" className={styles.moreLink}>
                    {T('article.more.browse')}
                  </Box>
                </Link>
              )}
            </Box>
          </Box>
        </Box>
      </PageLayout.SectionContainer>
    </PageLayout>
  );
};
